import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ActivityFeedDraftTypeEnum,
  IActivityFeedDraftComment,
  IActivityFeedDraftPost,
  IActivityFeedEditableDraftComment,
  IActivityFeedEditableDraftPost,
} from 'redux-store/api/activity-feed';

export interface IDraftSlice {
  unreadDraftsIds: number[];

  drafts: Array<IActivityFeedDraftPost | IActivityFeedDraftComment>;

  activePostDraftId: number;

  editablePostDraft: IActivityFeedEditableDraftPost;
  editableCommentDraft: IActivityFeedEditableDraftComment;
}

const initialState: IDraftSlice = {
  unreadDraftsIds: [],

  drafts: [],

  activePostDraftId: null,

  editablePostDraft: null,
  editableCommentDraft: null,
};

export const draftSlice = createSlice({
  name: 'draft',
  initialState,
  reducers: {
    setEditablePostDraft: (state, { payload }: PayloadAction<IActivityFeedEditableDraftPost>) => {
      state.editablePostDraft = payload;
    },

    setEditableCommentDraft: (
      state,
      { payload }: PayloadAction<IActivityFeedEditableDraftComment>
    ) => {
      state.editableCommentDraft = payload;
    },

    clearEditablePostDraft: (state) => {
      state.editablePostDraft = null;
    },

    clearEditableCommentDraft: (state) => {
      state.editableCommentDraft = null;
    },

    addDraftUnreadIds: (state, { payload }: PayloadAction<number[]>) => {
      state.unreadDraftsIds.push(...payload);
    },

    addDraftUnreadId: (state, { payload }: PayloadAction<number>) => {
      state.unreadDraftsIds.push(payload);
    },

    deleteDraftUnreadId: (state, { payload }: PayloadAction<number>) => {
      state.unreadDraftsIds = state.unreadDraftsIds.filter((id) => id !== payload);
    },

    addChangeDraftUnreadId: (state, { payload }: PayloadAction<number>) => {
      const isExist = state.unreadDraftsIds.includes(payload);
      if (isExist) return;
      state.unreadDraftsIds.push(payload);
    },

    resetDraftUnreadIds: (state) => {
      state.unreadDraftsIds = [];
    },

    setActivePostDraftId: (state, { payload }: PayloadAction<number>) => {
      state.activePostDraftId = payload;
    },

    removeActivePostDraftId: (state) => {
      state.activePostDraftId = null;
    },

    setDraft: (
      state,
      { payload }: PayloadAction<IActivityFeedDraftPost | IActivityFeedDraftComment>
    ) => {
      state.drafts.unshift(payload);
    },

    updateDraft: (
      state,
      { payload }: PayloadAction<IActivityFeedDraftPost | IActivityFeedDraftComment>
    ) => {
      if (payload.type === ActivityFeedDraftTypeEnum.POST) {
        const postIndex = state.drafts.findIndex(({ id }) => id === payload.id);

        if (postIndex >= 0) {
          state.drafts[postIndex] = Object.assign(state.drafts[postIndex], payload);
        }
      }

      if (payload.type === ActivityFeedDraftTypeEnum.COMMENT) {
        const commentIndex = state.drafts.findIndex(({ data }) => data.id === payload.data.id);

        if (commentIndex >= 0) {
          state.drafts[commentIndex] = Object.assign(state.drafts[commentIndex], payload);
        }
      }
    },

    deleteDraft: (
      state,
      { payload }: PayloadAction<{ id: number; type: 'DELETED_COMMENT' | 'DELETED_POST' }>
    ) => {
      if (payload.type === 'DELETED_POST') {
        state.drafts = state.drafts.filter(({ id }) => id !== payload.id);
      }

      if (payload.type === 'DELETED_COMMENT') {
        state.drafts = state.drafts.filter(({ data }) => data.id !== payload.id);
      }
    },

    resetDrafts: (state) => {
      state.drafts = [];
    },
  },
});

export const {
  addDraftUnreadId,
  addDraftUnreadIds,
  deleteDraftUnreadId,
  addChangeDraftUnreadId,
  resetDraftUnreadIds,

  setEditablePostDraft,
  setEditableCommentDraft,
  clearEditableCommentDraft,
  clearEditablePostDraft,

  setActivePostDraftId,
  removeActivePostDraftId,

  setDraft,
  updateDraft,
  deleteDraft,
  resetDrafts,
} = draftSlice.actions;

export default draftSlice.reducer;
